
import { type PropType, computed, defineComponent, getCurrentInstance, nextTick, ref } from "vue";

// Naming without c- prefix to avoid recursive lookups from vuetify-loader
import LtkHeaderMenuItems from "./LtkHeaderMenuItems.vue";
import LtkHeaderNavItems from "./LtkHeaderNavItems.vue";
import { type NavItem } from "./types";
import { CAppBar, CBtn, CContainer, CIcon, CList, CMenu, CSpacer, CToolbarItems } from "../../vuetify-components";
import CBtnPrimary from "../CBtnPrimary/CBtnPrimary.vue";
import CBtnSecondary from "../CBtnSecondary/CBtnSecondary.vue";
import CLtkLogo from "../CLtkLogo/CLtkLogo.vue";
import CSearchInput from "../CSearchInput/CSearchInput.vue";
import { type EmitSearchEnter, type SearchItem } from "../CSearchInput/types";

export default defineComponent({
  components: {
    CBtnPrimary,
    CBtnSecondary,
    CLtkLogo,
    LtkHeaderNavItems,
    LtkHeaderMenuItems,
    CSearchInput,
    CAppBar,
    CContainer,
    CMenu,
    CToolbarItems,
    CList,
    CBtn,
    CIcon,
    CSpacer,
  },
  inheritAttrs: false,
  props: {
    navItems: {
      type: Array as PropType<NavItem[]>,
      required: true,
    },
    userMenu: {
      type: Array as PropType<NavItem[]>,
      required: true,
    },
    loginMenu: {
      type: Array as PropType<NavItem[]>,
      required: true,
    },
    isLoggedIn: { default: false, type: Boolean },
    showSearchBtn: { default: true, type: Boolean },
    isImpersonating: { default: false, type: Boolean },
    nuxt: { default: false, type: Boolean },

    // Pass Through Props for CSearchInput
    searchSuggestions: {
      type: Array as PropType<SearchItem[]>,
      default: () => [],
    },
    searchTerm: {
      type: String,
      default: "",
    },
  },
  emits: {
    "menu-item-click": (_v: NavItem) => true,
    "menu-click": () => true,
    "login-click": () => true,
    "logo-click": () => true,
    "nav-click": (_v: NavItem) => true,
    "sign-up-click": () => true,
    "search-close": () => true,
    "search-click": (_v: string) => true,
    "search-enter": (_v: EmitSearchEnter) => true,
    "search-select": (_v: EmitSearchEnter) => true,
    "search-update": (_v: string) => true,
    "search-btn-click": (_v: boolean) => true,
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy;
    const searchInput = ref<typeof CSearchInput | undefined>();

    const isUserMenuOpened = ref(false);
    const isSearchMenuOpened = ref(false);

    const openSearch = () => {
      isSearchMenuOpened.value = !isSearchMenuOpened.value;
      emit("search-btn-click", isSearchMenuOpened.value);

      if (isSearchMenuOpened.value) {
        nextTick(() => {
          emit("search-click", props.searchTerm);
          searchInput.value?.autocomplete?.focus();
          searchInput.value?.autocomplete?.activateMenu();
        });
      }
    };

    const closeSearch = () => {
      emit("search-close");
      isSearchMenuOpened.value = false;
    };

    const onMenuItemClick = (event: NavItem) => {
      emit("menu-item-click", event);
      isUserMenuOpened.value = false;
    };

    const isMobile = computed(() => vm?.$vuetify.breakpoint.xsOnly);
    const isDesktop = computed(() => vm?.$vuetify.breakpoint.mdAndUp);
    const isLargeScreen = computed(() => vm?.$vuetify.breakpoint.lgAndUp);

    const menuIcon = computed(() => {
      const iconName = props.isLoggedIn && isDesktop.value ? "profile" : "menu";
      return `$${iconName}`;
    });

    return {
      menuIcon,
      searchInput,
      isUserMenuOpened,
      isSearchMenuOpened,
      isMobile,
      isDesktop,
      isLargeScreen,
      closeSearch,
      openSearch,
      onMenuItemClick,
    };
  },
});
